import React, { useState } from 'react';
import { Box, Paper, Typography, Button, IconButton, Chip, Collapse, Badge } from '@mui/material';
import { DateTimeRangePicker } from '@mui/x-date-pickers-pro/DateTimeRangePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

dayjs.extend(utc);
dayjs.extend(timezone);

const CompactDateFilter = ({
  onDateChange,
  initialDateFrom,
  initialDateTo,
  title = "Time Filter",
  vertical = false,
  defaultExpanded = false,
  inline = false,
}) => {
  
  const [expanded, setExpanded] = useState(defaultExpanded);
  const timezone = sessionStorage.getItem("timezone") || 'Etc/GMT';
  const [dateFrom, setDateFrom] = useState(() => {
    if (initialDateFrom) {
      return dayjs(initialDateFrom).tz(timezone);
    } else {
      return dayjs().tz(timezone).subtract(7, 'day');
    }
  });
  const [dateTo, setDateTo] = useState(() => {
    if (initialDateTo) {
      return dayjs(initialDateTo).tz(timezone);
    } else {
      return dayjs().tz(timezone);
    }
  });

  // Il filtro è sempre attivo se abbiamo delle date impostate
  const isFilterActive = dateFrom && dateTo;

  // Preset date ranges - più compatti
  const presets = [
    { label: '1H', hours: 1 },
    { label: '6H', hours: 6 },
    { label: '24H', hours: 24 },
    { label: '7D', days: 7 },
    { label: '30D', days: 30 }
  ];

  const handlePresetClick = (preset) => {
    const to = dayjs().tz(timezone);
    let from;
    
    if (preset.hours) {
      from = dayjs().tz(timezone).subtract(preset.hours, 'hour');
    } else {
      from = dayjs().tz(timezone).subtract(preset.days, 'day');
    }
    
    setDateFrom(from);
    setDateTo(to);
    
    if (onDateChange) {
      onDateChange({
        dateFrom: from,
        dateTo: to
      });
    }
  };




  if (inline) {
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Paper
          elevation={2}
          sx={{
            background: "rgba(0, 0, 0, 0.82)",
            backdropFilter: "blur(10px)",
            border: "1px solid #449095",
            borderRadius: 2,
          }}
        >
          {/* Single row: icon · title · chips · expand */}
          <Box display="flex" alignItems="center" gap={1} px={1.5} py={0.75}>
            <Badge
              variant="dot"
              sx={{
                "& .MuiBadge-dot": {
                  backgroundColor: isFilterActive ? "#ff9800" : "transparent",
                  width: 7,
                  height: 7,
                },
              }}
            >
              <CalendarTodayIcon sx={{ color: "#fff", fontSize: 17 }} />
            </Badge>
            <Typography
              variant="caption"
              fontWeight="bold"
              color="#fff"
              noWrap
              sx={{ mr: 0.5, flexShrink: 0 }}
            >
              {title}
            </Typography>
            <Box display="flex" gap={0.5} flex={1} flexWrap="nowrap" overflow="hidden">
              {presets.map((preset) => (
                <Chip
                  key={preset.label}
                  label={preset.label}
                  size="small"
                  onClick={(e) => { e.stopPropagation(); handlePresetClick(preset); }}
                  sx={{
                    fontSize: "0.75rem",
                    height: 24,
                    color: "#fff",
                    borderColor: "rgba(255,255,255,0.25)",
                    backgroundColor: "rgba(255,255,255,0.08)",
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.18)" },
                    "& .MuiChip-label": { px: 1 },
                    flexShrink: 0,
                  }}
                  variant="outlined"
                />
              ))}
            </Box>
            <IconButton
              size="small"
              sx={{ color: "#fff", p: 0.25, flexShrink: 0 }}
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? (
                <ExpandLessIcon sx={{ fontSize: 16 }} />
              ) : (
                <ExpandMoreIcon sx={{ fontSize: 16 }} />
              )}
            </IconButton>
          </Box>

          <Collapse in={expanded}>
            <Box p={1.5} pt={0.5} borderTop="1px solid rgba(255,255,255,0.08)">
              <Typography variant="caption" color="rgba(255,255,255,0.6)" mb={1} display="block">
                Custom Range:
              </Typography>
              <Box display="flex" flexDirection="column" gap={1}>
                <DateTimeRangePicker
                  value={[dateFrom, dateTo]}
                  onChange={([newDateFrom, newDateTo]) => {
                    if (newDateFrom && newDateTo) {
                      const fromDate = dayjs(newDateFrom).tz(timezone);
                      const toDate = dayjs(newDateTo).tz(timezone);
                      setDateFrom(fromDate);
                      setDateTo(toDate);
                      if (onDateChange) onDateChange({ dateFrom: fromDate, dateTo: toDate });
                    }
                  }}
                  ampm={false}
                  slotProps={{
                    textField: {
                      variant: "outlined",
                      size: "small",
                      fullWidth: true,
                      sx: {
                        "& .MuiOutlinedInput-root": {
                          color: "#fff",
                          fontSize: "0.875rem",
                          "& fieldset": { borderColor: "rgba(255,255,255,0.25)" },
                          "&:hover fieldset": { borderColor: "rgba(255,255,255,0.45)" },
                        },
                        "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.6)", fontSize: "0.875rem" },
                        "& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.6)" },
                      },
                    },
                  }}
                />
                <Button
                  variant="contained"
                  size="small"
                  fullWidth
                  onClick={() => onDateChange && onDateChange({ dateFrom, dateTo })}
                  sx={{
                    bgcolor: "rgba(255,255,255,0.08)",
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.2)",
                    "&:hover": { bgcolor: "rgba(68,144,149,0.25)" },
                  }}
                >
                  Apply Filter
                </Button>
              </Box>
            </Box>
          </Collapse>
        </Paper>
      </LocalizationProvider>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Paper
        elevation={vertical ? 1 : 2}
        sx={{
          position: vertical ? 'static' : 'absolute',
          top: vertical ? 'auto' : 20,
          left: vertical ? 'auto' : 20,
          zIndex: vertical ? 'auto' : 1000,
          background: vertical ? '#1e1e1e' : 'rgba(0, 0, 0, 0.85)',
          backdropFilter: vertical ? 'none' : 'blur(10px)',
          border: vertical ? '1px solid #459095' : '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: vertical ? 2 : 2,
          minWidth: vertical ? 280 : (expanded ? 350 : 280),
          maxWidth: vertical ? 280 : (expanded ? 400 : 300),
          height: vertical ? '100%' : 'auto',
          boxShadow: vertical ? '0 2px 8px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.5)',
          display: vertical ? 'flex' : 'block',
          flexDirection: vertical ? 'column' : 'initial',
        }}
      >
        {/* Header compatto */}
        <Box 
          display="flex" 
          alignItems="center" 
          justifyContent="space-between" 
          p={1.5}
          sx={{ cursor: 'pointer' }}
          onClick={() => setExpanded(!expanded)}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Badge
              variant="dot"
              sx={{
                '& .MuiBadge-dot': {
                  backgroundColor: isFilterActive ? '#ff9800' : 'transparent',
                  width: 8,
                  height: 8,
                }
              }}
            >
              <CalendarTodayIcon sx={{ color: '#fff', fontSize: 18 }} />
            </Badge>
            <Typography variant="subtitle2" fontWeight="bold" color="#fff">
              {title}
            </Typography>
          </Box>
          
          <Box display="flex" alignItems="center" gap={0.5}>
            <IconButton 
              size="small"
              sx={{ color: '#fff' }}
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        {/* Quick filters sempre visibili */}
        <Box px={1.5} pb={expanded ? 0 : 1.5}>
          <Box 
            display="flex" 
            flexWrap="wrap" 
            gap={0.5}
            flexDirection={vertical ? 'column' : 'row'}
          >
            {presets.map((preset) => (
              <Chip
                key={preset.label}
                label={preset.label}
                size="small"
                onClick={() => handlePresetClick(preset)}
                sx={{
                  fontSize: '0.75rem',
                  height: 24,
                  color: '#fff',
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    borderColor: 'rgba(255, 255, 255, 0.5)'
                  },
                  width: vertical ? '100%' : 'auto',
                  justifyContent: vertical ? 'flex-start' : 'center'
                }}
                variant="outlined"
              />
            ))}
          </Box>
        </Box>

        {/* Expanded content */}
        <Collapse in={expanded}>
          <Box p={1.5} pt={1}>
            <Typography variant="caption" color="rgba(255, 255, 255, 0.7)" mb={1} display="block">
              Custom Range:
            </Typography>
            
            <Box display="flex" flexDirection="column" gap={1}>              
              <DateTimeRangePicker
                value={[dateFrom, dateTo]}
                onChange={([newDateFrom, newDateTo]) => {
                  if (newDateFrom && newDateTo) {
                    // Assicurati che entrambe le date abbiano lo stesso timezone
                    const fromDate = dayjs(newDateFrom).tz(timezone);
                    const toDate = dayjs(newDateTo).tz(timezone);
                    setDateFrom(fromDate);
                    setDateTo(toDate);
                    
                    if (onDateChange) {
                      onDateChange({
                        dateFrom: fromDate,
                        dateTo: toDate
                      });
                    }
                  }
                }}
                ampm={false}
                slotProps={{
                  textField: {
                    variant: 'outlined',
                    size: 'small',
                    fullWidth: true,
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        color: '#fff',
                        fontSize: '0.875rem',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.5)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.7)',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(255, 255, 255, 0.7)',
                        fontSize: '0.875rem',
                      },
                      '& .MuiSvgIcon-root': {
                        color: 'rgba(255, 255, 255, 0.7)',
                      }
                    }
                  }
                }}
              />

              <Button
                variant="contained"
                size="small"
                fullWidth
                onClick={() => onDateChange && onDateChange({
                  dateFrom: dateFrom,
                  dateTo: dateTo
                })}
                sx={{
                  mt: 1,
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  color: '#fff',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    borderColor: 'rgba(255, 255, 255, 0.5)'
                  }
                }}
              >
                Apply Filter
              </Button>
            </Box>
          </Box>
        </Collapse>
        
        {/* Spacer per riempire lo spazio rimanente in modalità verticale */}
        {vertical && (
          <Box sx={{ flexGrow: 1 }} />
        )}
      </Paper>
    </LocalizationProvider>
  );
};

export default CompactDateFilter;